import React from 'react';
import {
  ChevronDown,
  Settings as SettingsIcon,
  School,
  IdCard,
  Calendar,
  Users,
  Shield,
  UserCog,
  ToggleLeft,
  FileText,
  Mail,
  MessageCircle,
  Image,
  CreditCard,
  CalendarRange,
  MessageSquare,
  ShieldAlert,
  Fingerprint,
} from 'lucide-react';
import { cn } from '../../lib/utils';

export const PLATFORM_SETTINGS_NAV = [
  {
    key: 'platform',
    label: 'Platform',
    items: [
      { id: 'platform.branding', label: 'Platform Branding', icon: Image },
      { id: 'platform.payment', label: 'Payment Gateway', icon: CreditCard },
      { id: 'platform.sms', label: 'SMS Gateway', icon: MessageSquare },
      { id: 'platform.financialYear', label: 'Financial Years', icon: CalendarRange },
      { id: 'platform.security', label: 'Security', icon: ShieldAlert },
    ],
  },
];

export const SETTINGS_NAV = [
  {
    key: 'billing',
    label: 'Billing',
    items: [
      { id: 'billing.subscription', label: 'Subscription', icon: CreditCard },
    ],
  },
  {
    key: 'general',
    label: 'General',
    items: [
      { id: 'general.settings', label: 'General Settings', icon: SettingsIcon },
      { id: 'general.school', label: 'School Information', icon: School },
      { id: 'general.idcard', label: 'ID Card Setup', icon: IdCard },
      { id: 'general.email', label: 'Email / SMTP', icon: Mail },
      { id: 'general.whatsapp', label: 'WhatsApp Configuration', icon: MessageCircle },
    ],
  },
  {
    key: 'features',
    label: 'Features',
    items: [
      { id: 'features.management', label: 'Feature Management', icon: ToggleLeft },
      { id: 'slips.templates', label: 'Slips', icon: FileText },
      { id: 'reports.certificates', label: 'Certificates', icon: FileText },
    ],
  },
  {
    key: 'academic',
    label: 'Academic',
    items: [
      { id: 'academic.year', label: 'Academic Year', icon: Calendar },
      { id: 'academic.attendance', label: 'Attendance', icon: Calendar },
      { id: 'academic.biometric', label: 'Biometric Attendance', icon: Fingerprint },
    ],
  },
  {
    key: 'roles',
    label: 'Roles',
    items: [
      { id: 'roles.userRoles', label: 'User Roles', icon: Users },
      { id: 'roles.permissions', label: 'Permissions', icon: Shield },
      { id: 'roles.staffAccess', label: 'Staff Access', icon: UserCog },
    ],
  },
];

export function getSettingsSectionMeta(sectionId) {
  for (const group of [...PLATFORM_SETTINGS_NAV, ...SETTINGS_NAV]) {
    const item = group.items.find((i) => i.id === sectionId);
    if (item) return { group: group.label, ...item };
  }
  return { group: 'General', label: 'Settings', icon: SettingsIcon };
}

export default function SettingsSidebar({
  navGroups = SETTINGS_NAV,
  activeSection,
  expandedGroup,
  onSelectSection,
  onToggleGroup,
  className,
}) {
  return (
    <aside
      className={cn(
        'w-full lg:w-64 shrink-0 rounded-2xl border border-border bg-card shadow-sm overflow-hidden',
        className
      )}
    >
      <div className="px-4 py-4 border-b border-border bg-muted/30">
        <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">Configuration</p>
        <p className="text-sm font-medium text-foreground mt-0.5">Settings menu</p>
      </div>
      <nav className="p-2 space-y-1">
        {navGroups.map((group) => {
          const isOpen = expandedGroup === group.key;
          const groupActive = group.items.some((item) => item.id === activeSection);
          return (
            <div key={group.key} className="rounded-xl overflow-hidden">
              <button
                type="button"
                onClick={() => onToggleGroup(group.key)}
                className={cn(
                  'w-full flex items-center gap-2 px-3 py-2.5 rounded-xl text-sm font-semibold transition-all duration-200',
                  groupActive
                    ? 'text-foreground bg-blue-500/10 dark:bg-blue-950/30'
                    : 'text-muted-foreground hover:text-foreground hover:bg-accent/70'
                )}
              >
                <span className="flex-1 text-left">{group.label}</span>
                <ChevronDown
                  className={cn(
                    'h-4 w-4 transition-transform duration-200',
                    isOpen ? 'rotate-180' : 'rotate-0'
                  )}
                />
              </button>
              <div
                className={cn(
                  'grid transition-[grid-template-rows] duration-300 ease-in-out',
                  isOpen ? 'grid-rows-[1fr]' : 'grid-rows-[0fr]'
                )}
              >
                <div className="overflow-hidden">
                  <ul className="pb-2 pt-1 space-y-0.5">
                    {group.items.map((item) => {
                      const Icon = item.icon;
                      const active = activeSection === item.id;
                      return (
                        <li key={item.id}>
                          <button
                            type="button"
                            onClick={() => onSelectSection(item.id)}
                            className={cn(
                              'relative w-full flex items-center gap-2.5 pl-4 pr-3 py-2 rounded-lg text-sm transition-all duration-200 text-left',
                              active
                                ? 'bg-blue-600 text-white font-semibold shadow-sm'
                                : 'text-muted-foreground hover:text-foreground hover:bg-accent/60 font-medium'
                            )}
                          >
                            {active && (
                              <span className="absolute left-1 top-1/2 -translate-y-1/2 h-5 w-1 rounded-full bg-blue-400" />
                            )}
                            <Icon className={cn('h-4 w-4 shrink-0', active ? 'text-white' : '')} />
                            <span>{item.label}</span>
                          </button>
                        </li>
                      );
                    })}
                  </ul>
                </div>
              </div>
            </div>
          );
        })}
      </nav>
    </aside>
  );
}
